import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Sun, Moon, AlertCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";
import { formatApiErrorDetail } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { isDark, toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setSuccess(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";
  const inputCls = isDark
    ? "bg-[#0A0A0A] border-white/[0.10] focus:border-[#FF6B00] text-[#F5F5F5] placeholder-white/25"
    : "bg-white border-black/[0.10] focus:border-[#FF6B00] text-[#0A0A0A] placeholder-black/25";

  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-4 relative`}>
      <button
        onClick={toggleTheme}
        className={`absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center border ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"}`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun size={15} className="text-[#F5F5F5]" /> : <Moon size={15} className="text-[#0A0A0A]" />}
      </button>

      <Link to="/" className="flex items-center gap-3 mb-8">
        <div className="bg-[#FF6B00] px-2.5 py-1" style={{ boxShadow: isDark ? "0 0 16px rgba(255,107,0,0.40)" : "none" }}>
          <span className="font-display font-black text-white text-lg uppercase tracking-wider">PGF</span>
        </div>
        <span className={`font-display font-black text-xl uppercase tracking-widest ${text}`}>Pregame Fantasy</span>
      </Link>

      <div className="w-full max-w-[440px] glass-card rounded-2xl p-7" data-testid="reset-password-page">
        <h1 className={`font-display font-black text-3xl uppercase mb-1 ${text}`}>New Password</h1>
        <p className={`font-body text-sm mb-6 ${muted}`}>Choose a strong one. We'll log you in next.</p>

        {!token && (
          <div className="bg-[#FF4444]/10 border border-[#FF4444]/40 rounded-xl px-4 py-3 flex items-center gap-2 mb-5">
            <AlertCircle size={15} className="text-[#FF4444] shrink-0" />
            <p className="text-[#FF4444] font-body text-sm">Reset link is missing or malformed.</p>
          </div>
        )}

        {success ? (
          <div className="bg-[#00C853]/10 border border-[#00C853]/40 rounded-xl px-4 py-3 flex items-start gap-2" data-testid="reset-success">
            <CheckCircle2 size={15} className="text-[#00C853] shrink-0 mt-0.5" />
            <p className="text-[#00C853] font-body text-sm">Password updated. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {error && (
              <div className="bg-[#FF4444]/10 border border-[#FF4444]/40 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="reset-error">
                <AlertCircle size={15} className="text-[#FF4444] shrink-0" />
                <p className="text-[#FF4444] font-body text-sm">{error}</p>
              </div>
            )}
            <div>
              <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>New Password</label>
              <input
                type="password" data-testid="reset-password-input"
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••" required
                className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`}
              />
            </div>
            <div>
              <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>Confirm Password</label>
              <input
                type="password" data-testid="reset-password-confirm"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                placeholder="••••••••" required
                className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`}
              />
            </div>
            <button
              type="submit" data-testid="reset-submit-btn" disabled={loading || !token}
              className="w-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-display font-black uppercase tracking-wide py-3 rounded-xl transition-all active:scale-[0.98]"
            >
              {loading ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
